import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useLoaderData,
	type MetaFunction,
} from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { floatingToolbarClassName } from '#app/components/floating-toolbar.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getNoteImgSrc, useIsPending } from '#app/utils/misc.tsx'
import { requireUserWithPermission } from '#app/utils/permissions.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { userHasPermission, useOptionalUser } from '#app/utils/user.ts'
import { type loader as characterLoader } from './characters.tsx'
import { brandName } from '#app/components/branding.js'

export async function loader({ params }: LoaderFunctionArgs) {
	const character = await prisma.character.findUnique({
		where: { id: params.characterId },
		select: {
			id: true,
			name: true,
			description: true,
			userId: true,
			createdAt: true,
			establishedAt: true,
			shard: {
				select: {
					name: true,
					slug: true,
					server: { select: { name: true, slug: true } },
				},
			},
		},
	})

	invariantResponse(character, 'Not found', { status: 404 })

	const date = new Date(character.createdAt)
	const timeAgo = formatDistanceToNow(date)

	return json({
		character,
		timeAgo,
	})
}

const DeleteFormSchema = z.object({
	intent: z.literal('delete-character'),
	characterId: z.string(),
})

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: DeleteFormSchema,
	})
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { characterId } = submission.value

	const character = await prisma.character.findFirst({
		select: { id: true, userId: true, user: { select: { username: true } } },
		where: { id: characterId },
	})
	invariantResponse(character, 'Not found', { status: 404 })

	const isOwner = character.userId === userId
	await requireUserWithPermission(
		request,
		isOwner ? `delete:note:own` : `delete:note:any`,
	)

	await prisma.character.delete({ where: { id: character.id } })

	return redirectWithToast(`/users/${character.user.username}/characters`, {
		type: 'success',
		title: 'Success',
		description: 'Your character has been deleted.',
	})
}

export default function CharacterRoute() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const isOwner = user?.id === data.character.userId
	const canDelete = userHasPermission(
		user,
		isOwner ? `delete:note:own` : `delete:note:any`,
	)
	const displayBar = canDelete || isOwner

	const shard = data.character.shard
	const server = shard?.server?.[0]

	return (
		<div className="flex flex-col">
			<h2 className="mb-1 text-xl font-bold md:text-2xl">{data.character.name}</h2>
			<div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
				<Icon name="clock" className="scale-110">
					{data.timeAgo} ago
				</Icon>
				{data.character.establishedAt && (
					<>
						<span className="text-muted-foreground/40">·</span>
						<span>Est. {new Date(data.character.establishedAt).toLocaleDateString()}</span>
					</>
				)}
			</div>

			{/* Server & Shard */}
			{shard && (
				<div className="mb-4 flex flex-wrap gap-2">
					{server && (
						<Link
							to={`/servers/${server.slug}`}
							className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
						>
							{server.name}
						</Link>
					)}
					<Link
						to={`/servers/${server?.slug}/shards/${shard.slug}`}
						className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted-foreground/10"
					>
						{shard.name}
					</Link>
				</div>
			)}

			{/* Description */}
			{data.character.description && (
				<div className="mb-4 rounded-lg border bg-background p-3 md:p-4">
					<p className="whitespace-break-spaces text-sm md:text-base">{data.character.description}</p>
				</div>
			)}

			{/* Actions */}
			{displayBar && (
				<div className="mt-2 flex items-center gap-2">
					<Button asChild size="sm">
						<Link to="edit">
							<Icon name="pencil-1" className="mr-1 scale-110">Edit</Icon>
						</Link>
					</Button>
					{canDelete ? <DeleteCharacter id={data.character.id} /> : null}
				</div>
			)}
		</div>
	)
}

export function DeleteCharacter({ id }: { id: string }) {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [form] = useForm({
		id: 'delete-character',
		lastResult: actionData?.result,
	})

	return (
		<Form method="POST" {...getFormProps(form)}>
			<input type="hidden" name="characterId" value={id} />
			<StatusButton
				type="submit"
				name="intent"
				value="delete-character"
				variant="destructive"
				status={isPending ? 'pending' : (form.status ?? 'idle')}
				disabled={isPending}
				className="w-full max-md:aspect-square max-md:px-0"
			>
				<Icon name="trash" className="scale-125 max-md:scale-150">
					<span className="max-md:hidden">Delete</span>
				</Icon>
			</StatusButton>
			<ErrorList errors={form.errors} id={form.errorId} />
		</Form>
	)
}

export const meta: MetaFunction<
	typeof loader,
	{ 'routes/users+/$username_+/characters': typeof characterLoader }
> = ({ data, params, matches }) => {
	const notesMatch = matches.find(
		(m) => m.id === 'routes/users+/$username_+/characters',
	)
	const displayName = notesMatch?.data?.owner.name ?? params.username
	const charName = data?.character.name ?? 'Character Name'
	const charContentsSummary = `Check out my character: ${charName}`
	return [
		{ title: `${charName} | ${displayName}'s Characters | ${brandName}` },
		{
			name: 'description',
			content: charContentsSummary,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>You are not allowed to do that</p>,
				404: ({ params }) => (
					<p>No character with the id "{params.characterId}" exists</p>
				),
			}}
		/>
	)
}
